import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { 
  NapLocationItem, 
  SleepEnvironmentItem, 
  SleepOnsetMethodItem, 
  RestfulnessRatingItem,
  LocationItem,
  EnvironmentItem,
  OnsetMethodItem,
  RestfulnessItem,
  SoundItem,
  SoundLevelItem
} from "@/types/activities/referenceItems";

/**
 * Custom hook to fetch and manage reference data for sleep and nap activities
 */
export function useActivityLookupData(activityType: 'sleep' | 'nap') {
  const { toast } = useToast();
  
  // Sleep lookup data
  const [locations, setLocations] = useState<LocationItem[]>([]);
  const [environments, setEnvironments] = useState<EnvironmentItem[]>([]);
  const [onsetMethods, setOnsetMethods] = useState<OnsetMethodItem[]>([]);
  const [sounds, setSounds] = useState<SoundItem[]>([]);
  const [soundLevels, setSoundLevels] = useState<SoundLevelItem[]>([]);
  const [restfulnessRatings, setRestfulnessRatings] = useState<RestfulnessItem[]>([]);
  
  // Nap lookup data
  const [napLocations, setNapLocations] = useState<NapLocationItem[]>([]);
  const [napEnvironments, setNapEnvironments] = useState<SleepEnvironmentItem[]>([]);
  const [napOnsetMethods, setNapOnsetMethods] = useState<SleepOnsetMethodItem[]>([]);
  const [napRestfulnessRatings, setNapRestfulnessRatings] = useState<RestfulnessRatingItem[]>([]);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  
  // Load data based on activity type
  useEffect(() => {
    const loadLookupData = async () => {
      setIsLoading(true);
      
      try {
        if (activityType === 'sleep') {
          const [
            { data: locationData },
            { data: environmentData },
            { data: onsetData },
            { data: soundData },
            { data: soundLevelData },
            { data: restfulnessData }
          ] = await Promise.all([
            supabase.from('nap_location').select('id, description').order('description'),
            supabase.from('environments').select('id, description').order('description'),
            supabase.from('onset_methods').select('id, method').order('method'),
            supabase.from('sleep_sounds').select('id, name').order('name'),
            supabase.from('sound_levels').select('id, level').order('level'),
            supabase.from('restfulness').select('id, rating').order('id')
          ]);
          
          if (locationData) setLocations(locationData);
          if (environmentData) setEnvironments(environmentData);
          if (onsetData) setOnsetMethods(onsetData);
          if (soundData) setSounds(soundData);
          if (soundLevelData) setSoundLevels(soundLevelData);
          if (restfulnessData) setRestfulnessRatings(restfulnessData as any);
        } else if (activityType === 'nap') {
          const [
            { data: locationData },
            { data: environmentData },
            { data: onsetData },
            { data: restfulnessData }
          ] = await Promise.all([
            supabase.from('nap_location').select('id, description').order('description'),
            supabase.from('sleep_environment').select('id, description').order('description'),
            supabase.from('sleep_onset_method').select('id, description').order('description'),
            supabase.from('restfulness_rating').select('id, description').order('description')
          ]);
          
          if (locationData) setNapLocations(locationData);
          if (environmentData) setNapEnvironments(environmentData);
          if (onsetData) setNapOnsetMethods(onsetData);
          if (restfulnessData) setNapRestfulnessRatings(restfulnessData);
        }
      } catch (error) {
        console.error(`Error loading ${activityType} lookup data:`, error);
        toast({
          variant: "destructive",
          title: "Error",
          description: `Failed to load ${activityType} tracking options`
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadLookupData();
  }, [activityType, toast]);
  
  return {
    // Common properties
    isLoading,
    
    // Sleep lookup data
    locations,
    environments,
    onsetMethods,
    sounds,
    soundLevels,
    restfulnessRatings,
    
    // Nap lookup data
    napLocations,
    napEnvironments,
    napOnsetMethods,
    napRestfulnessRatings
  };
}
